import { useAuth } from "@clerk/react"
import { useQueryClient } from "@tanstack/react-query"
import type { ChallengeRecord } from "../challenges/storage"
import type { FriendRequestRecord } from "../friends/storage"
import { useSessionMutation, useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import type { FriendCodeLookupResult, PublicPlayerProfile } from "../schemas"
import {
  fetchChallengeRecordsFromServer,
  fetchFriendRequestRecordsFromServer,
  fetchPublicPlayerProfileByFriendCodeFromServer,
  fetchPublicPlayerProfileByOwnerIdFromServer,
  syncChallengeRecordToServer,
  syncFriendRequestRecordToServer,
  syncPublicPlayerProfileToServer,
} from "./remote"

const DEFAULT_SOCIAL_REFRESH_INTERVAL_MS = 60_000
const PENDING_SOCIAL_REFRESH_INTERVAL_MS = 15_000

export type SocialSnapshot = {
  challenges: ChallengeRecord[]
  friendRequests: FriendRequestRecord[]
  publicProfiles: PublicPlayerProfile[]
}

export function useSocialSnapshotQuery(pendingCount: number, enabled: boolean) {
  const { userId } = useAuth()

  return useSessionQuery<SocialSnapshot>({
    enabled,
    queryFn: async ({ sessionToken, signal, userId: sessionUserId }) => {
      const [friendRequests, challenges] = await Promise.all([
        fetchFriendRequestRecordsFromServer(sessionToken, signal),
        fetchChallengeRecordsFromServer(sessionToken, signal),
      ])

      const acceptedFriendIds = Array.from(
        new Set(
          friendRequests.flatMap((request) => {
            if (request.status !== "accepted") return []
            if (request.inviterUserId === sessionUserId) {
              return [request.requesterUserId]
            }
            if (request.requesterUserId === sessionUserId) {
              return [request.inviterUserId]
            }
            return []
          }),
        ),
      )

      const publicProfiles = (
        await Promise.all(
          acceptedFriendIds.map((ownerUserId) =>
            fetchPublicPlayerProfileByOwnerIdFromServer(
              sessionToken,
              ownerUserId,
              signal,
            ).catch(() => null),
          ),
        )
      ).filter((profile): profile is PublicPlayerProfile => profile != null)

      return {
        challenges,
        friendRequests,
        publicProfiles,
      }
    },
    queryKey: queryKeys.social.snapshot(userId),
    refetchInterval:
      pendingCount > 0
        ? PENDING_SOCIAL_REFRESH_INTERVAL_MS
        : DEFAULT_SOCIAL_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })
}

export function usePublishPublicProfileMutation() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()

  return useSessionMutation({
    mutationFn: (record: PublicPlayerProfile, { sessionToken }) =>
      syncPublicPlayerProfileToServer(record, sessionToken),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.social.snapshot(userId),
      })
    },
  })
}

export function useSyncFriendRequestMutation() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()

  return useSessionMutation({
    mutationFn: (record: FriendRequestRecord, { sessionToken }) =>
      syncFriendRequestRecordToServer(record, sessionToken),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.social.snapshot(userId),
      })
    },
  })
}

export function useSyncChallengeMutation() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()

  return useSessionMutation({
    mutationFn: (record: ChallengeRecord, { sessionToken }) =>
      syncChallengeRecordToServer(record, sessionToken),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.social.snapshot(userId),
      })
    },
  })
}

export function useLookupPublicProfileByFriendCode() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()

  return async (
    friendCode: string,
    getSessionToken: () => Promise<string | null>,
  ): Promise<FriendCodeLookupResult | null> => {
    const normalizedFriendCode = friendCode.trim().toUpperCase()
    const sessionToken = await getSessionToken()
    if (!sessionToken) {
      throw new Error("Missing session token.")
    }

    return queryClient.fetchQuery({
      queryFn: ({ signal }) =>
        fetchPublicPlayerProfileByFriendCodeFromServer(
          sessionToken,
          normalizedFriendCode,
          signal,
        ),
      queryKey: queryKeys.social.profileByFriendCode(
        userId,
        normalizedFriendCode,
      ),
      staleTime: 30_000,
    })
  }
}
